import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * useRowSelection — Windows Explorer-style row selection
 * 
 * - Long-press (500ms) on a row enters selection mode
 * - Click → toggle single; Shift+click → range select; Ctrl/Cmd+click → toggle individual
 * - Ctrl/Cmd+A → select all visible; Escape → clear & exit selection mode
 */
export function useRowSelection<T extends string | number = string | number>(orderedIds: T[]) {
    const [selectedIds, setSelectedIds] = useState<Set<T>>(new Set());
    const [selectionMode, setSelectionMode] = useState(false);
    const lastSelectedIndexRef = useRef<number>(-1);
    const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const longPressFiredRef = useRef(false);
    const shiftHeldRef = useRef(false);

    useEffect(() => {
        const down = (e: KeyboardEvent) => { if (e.key === 'Shift') shiftHeldRef.current = true; };
        const up = (e: KeyboardEvent) => { if (e.key === 'Shift') shiftHeldRef.current = false; };
        document.addEventListener('keydown', down);
        document.addEventListener('keyup', up);
        return () => { document.removeEventListener('keydown', down); document.removeEventListener('keyup', up); };
    }, []);

    // ── Derive selectionMode from selectedIds automatically ──
    useEffect(() => {
        if (selectedIds.size > 0 && !selectionMode) {
            setSelectionMode(true);
        } else if (selectedIds.size === 0 && selectionMode) {
            setSelectionMode(false);
            lastSelectedIndexRef.current = -1;
        }
    }, [selectedIds.size, selectionMode]);

    // ── Exit selection mode when nothing is selected ──
    const clearSelection = useCallback(() => {
        setSelectedIds(new Set());
        setSelectionMode(false);
        lastSelectedIndexRef.current = -1;
    }, []);

    // ── Toggle a single item (with Shift-range support) ──
    const toggleSelect = useCallback((id: T) => {
        const idx = orderedIds.indexOf(id);

        if (shiftHeldRef.current && lastSelectedIndexRef.current >= 0 && idx >= 0) {
            const start = Math.min(lastSelectedIndexRef.current, idx);
            const end = Math.max(lastSelectedIndexRef.current, idx);
            setSelectedIds(prev => {
                const next = new Set(prev);
                for (let i = start; i <= end; i++) next.add(orderedIds[i]);
                return next;
            });
            lastSelectedIndexRef.current = idx;
            return;
        }

        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            if (next.size > 0) {
                lastSelectedIndexRef.current = idx;
            }
            return next;
        });
    }, [orderedIds]);

    // ── Select all visible (preserves selections not in current orderedIds view) ──
    const selectAll = useCallback(() => {
        if (!selectionMode) setSelectionMode(true);
        setSelectedIds(prev => {
            const next = new Set(prev);
            for (const id of orderedIds) next.add(id);
            return next;
        });
    }, [orderedIds, selectionMode]);

    // ── Toggle select all visible ──
    const toggleSelectAll = useCallback(() => {
        const isAllVisibleSelected = orderedIds.length > 0 && orderedIds.every(id => selectedIds.has(id));
        if (isAllVisibleSelected) {
            setSelectedIds(prev => {
                const next = new Set(prev);
                for (const id of orderedIds) next.delete(id);
                return next;
            });
        } else {
            selectAll();
        }
    }, [selectedIds, orderedIds, selectAll]);

    // ── Handle row click with Ctrl/Shift support ──
    const handleRowSelect = useCallback((id: T, event?: React.MouseEvent) => {
        const idx = orderedIds.indexOf(id);
        const isShift = event?.shiftKey || shiftHeldRef.current;
        const isCtrl = event?.ctrlKey || event?.metaKey;

        if (isShift && lastSelectedIndexRef.current >= 0 && idx >= 0) {
            event?.preventDefault();
            const start = Math.min(lastSelectedIndexRef.current, idx);
            const end = Math.max(lastSelectedIndexRef.current, idx);
            setSelectedIds(prev => {
                const next = new Set(prev);
                for (let i = start; i <= end; i++) next.add(orderedIds[i]);
                return next;
            });
            if (!selectionMode) setSelectionMode(true);
            lastSelectedIndexRef.current = idx;
        } else if (isCtrl) {
            toggleSelect(id);
        } else {
            toggleSelect(id);
        }
    }, [selectionMode, orderedIds, toggleSelect]);

    // ── Long-press handlers for a row ──
    const handleRowMouseDown = useCallback((id: T, event: React.MouseEvent) => {
        // Only trigger on left-click, not on interactive elements
        if (event.button !== 0) return;
        const target = event.target as HTMLElement;
        if (target.closest('button, a, input, [role="menuitem"], [data-no-select]')) return;

        longPressFiredRef.current = false;

        longPressTimerRef.current = setTimeout(() => {
            longPressFiredRef.current = true;
            setSelectionMode(true);
            setSelectedIds(prev => {
                const next = new Set(prev);
                next.add(id);
                return next;
            });
            lastSelectedIndexRef.current = orderedIds.indexOf(id);
        }, 500);
    }, [orderedIds]);

    const handleRowMouseUp = useCallback(() => {
        if (longPressTimerRef.current) {
            clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
        }
    }, []);

    const handleRowMouseLeave = useCallback(() => {
        if (longPressTimerRef.current) {
            clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
        }
    }, []);

    // Whether the last mousedown resulted in a long-press (used to prevent navigation)
    const wasLongPress = useCallback(() => longPressFiredRef.current, []);

    // ── Keyboard shortcuts: Escape to exit, Ctrl/Cmd+A to select all ──
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && selectionMode) {
                clearSelection();
            }
            if (e.key === 'a' && (e.ctrlKey || e.metaKey) && selectionMode) {
                e.preventDefault();
                selectAll();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [selectionMode, clearSelection, selectAll]);

    // ── Cleanup timer on unmount ──
    useEffect(() => {
        return () => {
            if (longPressTimerRef.current) {
                clearTimeout(longPressTimerRef.current);
            }
        };
    }, []);

    return {
        selectedIds,
        selectionMode,
        toggleSelect,
        toggleSelectAll,
        selectAll,
        clearSelection,
        handleRowSelect,
        handleRowMouseDown,
        handleRowMouseUp,
        handleRowMouseLeave,
        wasLongPress,
        selectedCount: selectedIds.size,
        allSelected: orderedIds.length > 0 && orderedIds.every((id) => selectedIds.has(id)),
    };
}
